import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Favorite, FavoritesState } from '../../types';

// ensureValidState helper removed - was unused
const normalizeTicker = (value: string | undefined | null): string =>
  (value ?? '').toString().trim().toUpperCase();

const initialState: FavoritesState = {
  favorites: [],
  loading: false,
  error: null,
};

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setFavorites: (state, action: PayloadAction<Favorite[]>) => {
      state.favorites = Array.isArray(action.payload) ? action.payload : [];
      state.loading = false;
      state.error = null;
    },
    addFavorite: (state, action: PayloadAction<Favorite>) => {
      // Ensure favorites is an array
      if (!Array.isArray(state.favorites)) {
        state.favorites = [];
      }
      
      // Add the favorite if it doesn't already exist
      const existingIndex = state.favorites.findIndex(fav => fav.ticker === action.payload.ticker);
      if (existingIndex === -1) {
        state.favorites.push(action.payload);
      }
      state.loading = false;
      state.error = null;
    },
    updateFavorite: (state, action: PayloadAction<Favorite>) => {
      // Ensure favorites is an array
      if (!Array.isArray(state.favorites)) {
        state.favorites = [];
      }
      // Find by either id or ticker for more robust matching
      const index = state.favorites.findIndex(fav => 
        fav.id === action.payload.id || fav.ticker === action.payload.ticker
      );
      if (index !== -1) {
        state.favorites[index] = action.payload;
        console.log(`Updated favorite ${action.payload.ticker} in Redux state:`, action.payload);
      } else {
        console.warn(`Could not find favorite to update:`, action.payload);
      }
      state.loading = false;
      state.error = null;
    },
    removeFavorite: (state, action: PayloadAction<string>) => {
      // Ensure favorites is an array
      if (!Array.isArray(state.favorites)) {
        state.favorites = [];
      }
      // Support both legacy call sites:
      // - some dispatchers pass `fav.id`
      // - others pass `ticker` (e.g. ChartModal mobile + removeFavoriteThunk)
      const target = normalizeTicker(action.payload);
      state.favorites = state.favorites.filter((fav) => {
        const ticker = normalizeTicker(fav.ticker);
        return fav.id !== action.payload && ticker !== target;
      });
      state.loading = false;
      state.error = null;
    },
    reorderFavorites: (state, action: PayloadAction<{ sourceIndex: number; destinationIndex: number }>) => {
      const { sourceIndex, destinationIndex } = action.payload;
      const [removed] = state.favorites.splice(sourceIndex, 1);
      if (removed) {
        state.favorites.splice(destinationIndex, 0, removed);
        
        // Update order values
        state.favorites.forEach((favorite, index) => {
          favorite.order = index;
        });
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    /** Clear favorites on logout so the next session never inherits the prior user's list. */
    resetFavorites: () => initialState,
  },
});

export const {
  setLoading,
  setError,
  setFavorites,
  addFavorite,
  updateFavorite,
  removeFavorite,
  reorderFavorites,
  clearError,
  resetFavorites,
} = favoritesSlice.actions;

export default favoritesSlice.reducer;